// src/features/executive/insight/widgets/PromotionBar.tsx
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useTranslation } from 'react-i18next'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { formatVariance } from 'utils/executive.utils'
import { usePromotion } from '../hooks/use-promotion'

const fmt = (n: number) => new Intl.NumberFormat('th-TH').format(Math.round(n || 0))

// Rollup + "no promo" rows are muted so they don't compete with admin-named
// campaign groups for the eye.
const isAuxRow = (key: string) => key === 'ungrouped_rollup' || key === 'none'

// Campaigns are inherently volatile (new launches ramp fast, old ones retire),
// so the mover threshold is higher than for stable categories like Gender or
// Nationality (20%) to avoid noise.
const MOVER_MIN_VARIANCE = 0.3

// Coverage thresholds for the penetration insight chip — "strong coverage"
// when most visits touch a promo, "weak coverage" when very few do. Between
// these we stay silent (normal range).
const PENETRATION_STRONG_PCT = 50
const PENETRATION_WEAK_PCT = 20

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function PromotionBar() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = usePromotion()

  const items = data?.items ?? []
  const isEmpty = items.length === 0
  const total = data?.total ?? 0
  const previousTotal = data?.previousTotal ?? null
  const promoUsageCount = data?.promoUsageCount ?? 0
  const totalVariance = varianceOf(total, previousTotal)

  // Campaign coverage / penetration.
  const penetrationPct = total > 0 ? (promoUsageCount / total) * 100 : 0
  const penetrationStrong = penetrationPct >= PENETRATION_STRONG_PCT
  const penetrationWeak = total > 0 && penetrationPct < PENETRATION_WEAK_PCT

  // Largest mover among non-aux rows (skip 'none' & 'ungrouped_rollup').
  const mover = items
    .filter((i) => !isAuxRow(i.key) && i.previousCount != null && i.previousCount > 0)
    .map((i) => ({ i, v: varianceOf(i.count, i.previousCount) }))
    .filter((x) => x.v != null && Math.abs(x.v as number) >= MOVER_MIN_VARIANCE)
    .sort((a, b) => Math.abs(b.v as number) - Math.abs(a.v as number))[0]

  // Bar width normalized to the biggest segment so even small leaders show.
  const maxPct = items.length === 0 ? 1 : Math.max(...items.map((i) => i.percent), 1)

  const subtitle = (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <span>
        {t('EXECUTIVE.INSIGHT.PROMOTION.SUBTITLE', {
          count: total,
          defaultValue: 'นับเป็น Visit (คนไข้ × วัน) — จาก {{count}} visit ทั้งหมด',
        })}
      </span>
      {totalVariance != null && (
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
          color={totalVariance >= 0 ? 'success.main' : 'error.main'}
        >
          {totalVariance >= 0
            ? <TrendingUpIcon sx={{ fontSize: 13, mr: 0.25 }} />
            : <TrendingDownIcon sx={{ fontSize: 13, mr: 0.25 }} />}
          {formatVariance(totalVariance)}
        </Typography>
      )}
    </Box>
  )

  const notice = (penetrationStrong || penetrationWeak || mover) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {penetrationStrong && (
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.PROMOTION.COVERAGE_STRONG',
                      'การใช้โปรฯ {{pct}}% ของ visits — campaign coverage แข็งแกร่ง',
                      { pct: penetrationPct.toFixed(0) }
                    )}
                  </Typography>
                }
                color="success"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {penetrationWeak && (
              <Chip
                icon={<WarningAmberOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.PROMOTION.COVERAGE_WEAK',
                      'ใช้โปรฯ เพียง {{pct}}% ของ visits — พิจารณาขยาย campaign coverage',
                      { pct: penetrationPct.toFixed(0) }
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {mover && (mover.v as number) >= 0 && (
              <Chip
                icon={<TrendingUpIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.PROMOTION.MOVER_UP',
                      'แคมเปญมาแรง: {{label}} ▲ {{pct}}',
                      { label: mover.i.name, pct: formatVariance(mover.v as number) }
                    )}
                  </Typography>
                }
                color="info"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {mover && (mover.v as number) < 0 && (
              <Chip
                icon={<TrendingDownIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.PROMOTION.MOVER_DOWN',
                      'แคมเปญลดลง: {{label}} ▼ {{pct}}',
                      { label: mover.i.name, pct: formatVariance(mover.v as number) }
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
    </Stack>
  ) : null

  return (
    <WidgetCard
      title={t('EXECUTIVE.INSIGHT.PROMOTION.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.PROMOTION.SOURCE_TOOLTIP',
        'Visit = 1 คนไข้ × 1 วัน — มาวันเดียวกันมีหลาย orders นับ 1 visit\n• ถ้าตั้ง Promotion Grouping ที่ "ตั้งค่า → โปรโมชั่น" จะแสดงตาม group (1 visit อาจอยู่หลาย groups → % รวมเกิน 100% ได้)\n• "ไม่อยู่ในกลุ่ม" = visits ที่ใช้โปรฯ ที่ไม่ได้ assign เข้ากลุ่มใด — admin ควร fold เข้า group\n• "ไม่ใช้โปรโมชั่น" = visits ที่ไม่มี promo เลย (cohort untouched)'
      )}
      subtitle={subtitle}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.PROMOTION.EMPTY',
        'ไม่พบ visits ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      onRetry={refetch}
      skeletonHeight={300}
    >
      <Stack spacing={1.25}>
          {items.map((i) => {
            const variance = varianceOf(i.count, i.previousCount)
            // Tooltip enrichment: rollup row → list of promos that fell out;
            // group row → list of promotion ids covered (just count is enough).
            const tooltip =
              i.key === 'ungrouped_rollup' && i.promotionNames && i.promotionNames.length > 0
                ? `${i.name}\n• ${i.promotionNames.join('\n• ')}`
                : i.promotionIds && i.promotionIds.length > 1
                  ? `${i.name} (รวม ${i.promotionIds.length} โปรโมชั่น)`
                  : i.name
            return (
              <Box key={i.key}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, gap: 1 }}>
                  <Tooltip
                    title={tooltip}
                    placement="top-start"
                    enterDelay={400}
                    componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: isAuxRow(i.key) ? 400 : 500,
                        fontStyle: isAuxRow(i.key) ? 'italic' : 'normal',
                        color: isAuxRow(i.key) ? 'text.secondary' : 'text.primary',
                        cursor: i.key === 'ungrouped_rollup' ? 'help' : 'default',
                      }}
                    >
                      {i.name}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography variant="body2" color="text.primary">
                      {fmt(i.count)} ({i.percent.toFixed(1)}%)
                    </Typography>
                    {variance != null && (
                      <Typography
                        variant="caption"
                        color={variance >= 0 ? 'success.main' : 'error.main'}
                        sx={{ minWidth: 52, textAlign: 'right' }}
                      >
                        {formatVariance(variance)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${(i.percent / maxPct) * 100}%`,
                      height: '100%',
                      backgroundColor: isAuxRow(i.key) ? '#90a4ae' : '#e67e22',
                      borderRadius: 1,
                      transition: 'width 200ms ease',
                    }}
                  />
                </Box>
              </Box>
            )
          })}
      </Stack>
    </WidgetCard>
  )
}
